import React, { useState } from 'react';
import { Client } from '@/types/client';
import { useTranslation } from 'react-i18next';
import Grid, { itemList, itemText } from '@/components/molecules/section/Grid';
import { Modal } from '@/types/modal';
import Container from '@/components/molecules/section/Container';
import {
  useClientCanceledSales,
  useClientChatModerateStats,
  useClientLastLoginAttempt,
  useClientReliabilityStats,
  useClientSiftScoreById,
} from '@/hooks/client';
import { ServiceErrorView } from '@/components/atoms/Error';
import { Skeleton } from '@mui/material';
import ModalView from '@/components/modals/Modal';

const DEFAULT_EMPTY = '-';

const View: React.FC<{ client: Client }> = ({ client }) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState<Modal | null>(null);
  const [open, setOpen] = useState(false);

  const siftScore = useClientSiftScoreById(Number(client.clientId), false);
  const data = useClientChatModerateStats(Number(client.clientId), open);

  const { data: clientCanceledSales, isFetching: clientCanceledSaleFetching } = useClientCanceledSales(
    Number(client.clientId),
    1,
    10,
    open,
  );

  const lastLoginAttemptQuery = useClientLastLoginAttempt(client.clientId);
  const reliabilityStatsQuery = useClientReliabilityStats(Number(client.clientId), open);
  const reliabilityStats = reliabilityStatsQuery.kind === 'Loaded' ? reliabilityStatsQuery.data : null;

  if (siftScore.kind === 'Loaded') {
    client.siftScore = siftScore.data.siftScore ?? null;
  }

  let unShippedItems = '';

  if (clientCanceledSales) {
    unShippedItems = String(clientCanceledSales.total || DEFAULT_EMPTY) ?? DEFAULT_EMPTY;
  }

  const lastLoginAttempt =
    lastLoginAttemptQuery.kind === 'Loaded'
      ? lastLoginAttemptQuery.data.lastLoginAttemptCountry || DEFAULT_EMPTY
      : DEFAULT_EMPTY;

  return (
    <>
      <Container
        title={t('client.fraud.title')}
        actions={[]}
        handler={{ open, setOpen }}
        testId="fraud"
      >
        <>
          {data.kind === 'Called' && data.fetching === true && clientCanceledSaleFetching && (
            <div>
              <Skeleton
                height={80}
                variant="rounded"
              />
            </div>
          )}
          {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
          {data.kind === 'Loaded' && (
            <Grid
              column={5}
              items={[
                itemText(
                  t('client.fraud.sift_score'),
                  undefined !== client.siftScore ? `${client.siftScore}` : '--',
                  {
                    kind: 'Button',
                    label: t('common.show_score').toUpperCase(),
                    onClick: () => {
                      siftScore.refetch();
                    },
                  },
                  undefined,
                  'number' === typeof client.siftScore && client.siftScore >= 40 ? '#ff5555' : undefined,
                ),
                itemText(t('client.fraud.total_gmv'), reliabilityStats?.totalGMV ?? DEFAULT_EMPTY),
                itemText(t('client.fraud.purchased_value'), reliabilityStats?.totalPurchasedAmount ?? DEFAULT_EMPTY),
                itemText(
                  t('client.fraud.number_of_counterfeit_sales'),
                  reliabilityStats?.counterfeitSaleCount ?? DEFAULT_EMPTY,
                ),
                itemList(t('client.fraud.counterfeit_items_ref'), reliabilityStats?.counterfeitItems ?? null),
                itemText(
                  t('client.fraud.number_of_refused_items'),
                  reliabilityStats?.refusedItemsCount ?? DEFAULT_EMPTY,
                ),
                itemText(t('client.fraud.unshipped_items'), unShippedItems, {
                  kind: 'Button',
                  label: t('common.see_details').toUpperCase(),
                  onClick: () => setModal({ kind: 'ClientCanceledSales', client, onClose: () => setModal(null) }),
                }),
                itemText(
                  t('client.fraud.ban_proxy'),
                  data.data.reasonLastBan !== null ? data.data.reasonLastBan : 'null',
                ),
                itemText(t('client.fraud.unbans'), data.data.nbUnbans),
                itemText(t('client.fraud.bans'), data.data.nbBans),
                itemText(t('client.fraud.reports'), data.data.nbReports),
                itemText(
                  t('client.fraud.last_login_attempt'),
                  lastLoginAttempt,
                  {
                    kind: 'Button',
                    label: t('common.show').toUpperCase(),
                    disabled: lastLoginAttemptQuery.kind === 'Called' && lastLoginAttemptQuery.fetching,
                    onClick: () => {
                      lastLoginAttemptQuery.refetch();
                    },
                  },
                  undefined,
                  'number' === typeof client.siftScore && client.siftScore >= 40 ? '#ff5555' : undefined,
                ),
              ]}
            />
          )}
        </>
      </Container>
      <ModalView modal={modal} />
    </>
  );
};
export default View;
